import * as React from 'react'
import withHydrationOnDemand from 'react-hydration-on-demand'
import dynamic from 'next/dynamic'

import { useI18n, useSiteConfig } from '@thg-commerce/enterprise-core'
import { AvailabilitySource } from '@thg-commerce/enterprise-network/src/generated/graphql'
import { useTheme } from '@thg-commerce/enterprise-pages/src/Product/theme'
import { spacing } from '@thg-commerce/enterprise-theme'
import { InfoBoxesProps } from '@thg-commerce/gravity-patterns'
import { TabProps } from '@thg-commerce/gravity-patterns/Tabs/Tabs'

import { useProductPage } from '../../context'
import { DeliveryReturnsModalPresenter } from '../DeliveryReturnsModalPresenter'
import { deliveryAndReturnsModalSectionIds } from '../DeliveryReturnsModalPresenter/DeliveryReturnsModal/DeliveryReturnsModal'
import DeliveryInformation from '../USPDeliveryInfo/DeliveryInformation'

const InfoBoxes = dynamic<InfoBoxesProps>(() =>
  import('@thg-commerce/gravity-patterns/InfoBoxes').then(
    (mod) => mod.InfoBoxes,
  ),
)

const InfoDeliveryIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/InfoDeliveryIcon'),
)
const InfoClickAndCollect = dynamic(
  () =>
    import('@thg-commerce/gravity-icons/src/components/InfoClickAndCollect'),
)
const InfoFreeReturn = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/InfoFreeReturn'),
)
const SvgIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/SvgIcon'),
  { ssr: true, loading: () => <div style={{ width: 24, height: 24 }} /> },
)

const InfoBoxesComponent = withHydrationOnDemand({
  on: [['delay', 500]],
})(InfoBoxes)

export interface ProductInfoBoxesProps {
  headerHeight: number
}

export const ProductInfoBoxes = (props: ProductInfoBoxesProps) => {
  const {
    productPageShowDeliveryInfoBoxes,
    showClickAndCollectProductInfoBox,
    showClickAndCollectProductTab,
    showUSPDeliveryInformation,
    deliveryContentKeys,
  } = useSiteConfig()
  const { product, selectedVariant } = useProductPage()

  const i18n = useI18n()
  const theme = useTheme()

  const [
    isDeliveryReturnsModalOpen,
    setDeliveryReturnsModalOpen,
  ] = React.useState(false)
  const [tabs, setTabs] = React.useState<TabProps[]>()
  const [activeTabId, setActiveTabId] = React.useState<string>()
  const [focusedSectionId, setFocusedSectionId] = React.useState<string>()
  const tabIndex = React.useMemo<number>(
    () => tabs?.findIndex((tab) => tab.id === activeTabId) || 0,
    [tabs],
  )

  const handleInfoButtonClick = React.useCallback(
    (tabId: string) => {
      setActiveTabId(tabId)
      setDeliveryReturnsModalOpen(true)
    },
    [tabs],
  )

  if (
    !productPageShowDeliveryInfoBoxes ||
    product?.allVariantsOutOfStock ||
    (product?.defaultVariant?.availabilitySource !==
      AvailabilitySource.Warehouse &&
      !product?.defaultVariant?.deliveryReturnsContentDefined)
  ) {
    return null
  }
  const renderIcon = (iconPath?: string, label?: string) => {
    if (iconPath) {
      return (
        <SvgIcon
          xmlns="http://www.w3.org/2000/svg"
          viewBox={theme.pageTheme?.productInfoBoxes.icons.viewBox}
          width={theme.pageTheme?.productInfoBoxes.icons.width}
          height={theme.pageTheme?.productInfoBoxes.icons.height}
        >
          <path d={iconPath} fillRule="evenodd" />
        </SvgIcon>
      )
    }

    switch (label) {
      case 'delivery':
        return <InfoDeliveryIcon aria-label={label} />
      case 'clickAndCollect':
        return <InfoClickAndCollect aria-label={label} />
      case 'returns':
        return <InfoFreeReturn aria-label={label} />
      default:
        return null
    }
  }

  const generateInfoBoxItems = (
    i18nText: {
      deliveryTitle: string
      deliveryButtonText: string
      deliveryDescription: string
      clickAndCollectTitle?: string
      clickAndCollectButtonText?: string
      clickAndCollectDescription?: string
      returnsTitle: string
      returnsButtonText: string
      returnsDescription: string
    },
    sourceType: AvailabilitySource,
  ) => {
    const isWarehouse = sourceType === AvailabilitySource.Warehouse

    return [
      {
        title: i18nText.deliveryTitle,
        image: renderIcon(
          theme.pageTheme?.productInfoBoxes.icons.delivery.svgPath,
          'delivery',
        ),
        buttonText: i18nText.deliveryButtonText,
        buttonOnClick: () =>
          handleInfoButtonClick(deliveryAndReturnsModalSectionIds.delivery),
        description: i18nText.deliveryDescription,
      },
      ...(isWarehouse && showClickAndCollectProductInfoBox
        ? [
            {
              title: i18nText.clickAndCollectTitle,
              image: renderIcon(
                theme.pageTheme?.productInfoBoxes.icons.clickAndCollect.svgPath,
                'clickAndCollect',
              ),
              buttonText: i18nText.clickAndCollectButtonText,
              buttonOnClick: () => {
                handleInfoButtonClick(
                  showClickAndCollectProductTab
                    ? deliveryAndReturnsModalSectionIds.clickAndCollect
                    : deliveryAndReturnsModalSectionIds.delivery,
                )
                setFocusedSectionId(
                  deliveryAndReturnsModalSectionIds.clickAndCollect,
                )
              },
              description: i18nText.clickAndCollectDescription,
            },
          ]
        : []),
      {
        title: i18nText.returnsTitle,
        image: renderIcon(
          theme.pageTheme?.productInfoBoxes.icons.returns.svgPath,
          'returns',
        ),
        buttonText: i18nText.returnsButtonText,
        buttonOnClick: () =>
          handleInfoButtonClick(deliveryAndReturnsModalSectionIds.returns),
        description: i18nText.returnsDescription,
      },
    ]
  }

  const infoTextWarehouse = {
    deliveryTitle: i18n('product.infoboxes.warehouse.delivery.title'),
    deliveryButtonText: i18n('product.infoboxes.warehouse.delivery.buttontext'),
    deliveryDescription: i18n(
      'product.infoboxes.warehouse.delivery.description',
    ),
    clickAndCollectTitle: i18n('product.infoboxes.clickandcollect.title'),
    clickAndCollectButtonText: i18n(
      'product.infoboxes.clickandcollect.buttontext',
    ),
    clickAndCollectDescription: i18n(
      'product.infoboxes.clickandcollect.description',
    ),
    returnsTitle: i18n('product.infoboxes.warehouse.freereturn.title'),
    returnsButtonText: i18n(
      'product.infoboxes.warehouse.freereturn.buttontext',
    ),
    returnsDescription: i18n(
      'product.infoboxes.warehouse.freereturn.description',
    ),
  }

  const infoTextDropship = {
    deliveryTitle: i18n('product.infoboxes.dropship.delivery.title'),
    deliveryButtonText: i18n('product.infoboxes.dropship.delivery.buttontext'),
    deliveryDescription: i18n(
      'product.infoboxes.dropship.delivery.description',
    ),
    returnsTitle: i18n('product.infoboxes.dropship.freereturn.title'),
    returnsButtonText: i18n('product.infoboxes.dropship.freereturn.buttontext'),
    returnsDescription: i18n(
      'product.infoboxes.dropship.freereturn.description',
    ),
  }
  const viewMoreText = i18n('product.usp.delivery.viewmore.text')

  const infoBoxes =
    selectedVariant?.availabilitySource === AvailabilitySource.Warehouse
      ? generateInfoBoxItems(infoTextWarehouse, AvailabilitySource.Warehouse)
      : generateInfoBoxItems(infoTextDropship, AvailabilitySource.Dropship)

  const modalRichContent =
    selectedVariant?.availabilitySource === AvailabilitySource.Warehouse
      ? undefined
      : {
          returnsInformation: selectedVariant?.returnsInformation ?? '',
          deliveryInformation: selectedVariant?.deliveryInformation ?? '',
        }

  return (
    <React.Fragment>
      {showUSPDeliveryInformation ? (
        <DeliveryInformation
          content={product.deliveryInformationContent}
          buttonOnClick={() =>
            handleInfoButtonClick(deliveryAndReturnsModalSectionIds.delivery)
          }
          viewMoreText={viewMoreText}
        />
      ) : (
        <InfoBoxesComponent
          items={infoBoxes}
          wrapperProps={{
            style: { width: '100%', marginBottom: spacing(4) },
          }}
        />
      )}
      <DeliveryReturnsModalPresenter
        isOpen={isDeliveryReturnsModalOpen}
        setIsOpen={setDeliveryReturnsModalOpen}
        headerHeight={props.headerHeight}
        dropshipRichContent={modalRichContent}
        tabIndex={tabIndex}
        focusedSectionId={focusedSectionId}
        setTabs={setTabs}
      />
    </React.Fragment>
  )
}
